import {
  AuthorizationResultFragmentFragment,
  MilestoneFragmentFragment,
  ProjectType
} from '@lopo-tech/lopo-api'
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import Icon from 'src/components/forms/icon/icon'
import { MarkdownRenderer } from 'src/components/markdown_renderer/markdown_renderer'
import NavigationItem from 'src/components/navigation_item/navigation_item'
import { CLASS_PREFIX } from 'src/constants'
import { getProjectRoutes } from 'src/constants/urls'
import { textResources } from 'src/lang/de'
import { COLOR_TYPE_GRAY_TINT_14, COLOR_TYPE_WHITE } from 'src/utils/color'
import DateFormatter from 'src/utils/date_formatter/date_formatter'
import { makeHTMLLinksFromText } from 'src/utils/string/string'
import { DetailCard } from '../project_detail/detail_card/detail_card'
import { ProjectMilestoneCreateModal } from './project_milestone_create_modal'
import './project_milestones.scss'

type Props = {
  projectId: string;
  projectSlug: string;
  projectType: ProjectType;
  milestones: MilestoneFragmentFragment[];
  canCreateMilestones: AuthorizationResultFragmentFragment;
  withPadding?: boolean;
  withShowAllFooter?: boolean;
  withSectionTitle: boolean;
};

const cls = `${CLASS_PREFIX}project-milestones`
const t = textResources.projects

export const ProjectMilestones: React.FC<Props> = ({
  projectId,
  projectSlug,
  projectType,
  milestones,
  canCreateMilestones,
  withPadding,
  withShowAllFooter,
  withSectionTitle
}) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingMilestone, setEditingMilestone] = useState<MilestoneFragmentFragment | null>(null)
  const history = useHistory()
  const routes = getProjectRoutes(projectType)

  return (
    <DetailCard withPadding={withPadding}>
      <div className={`${cls}__header`}>
        {withSectionTitle && (
          <h2 className={`${cls}__section-title`}>{t.milestones.title}</h2>
        )}
        {canCreateMilestones?.value && (
          <button
            type="button"
            className={`${cls}__create-btn`}
            onClick={() => setIsCreateModalOpen(true)}
          >
            <span className={`${cls}__create-btn-icon`} aria-hidden>
              <Icon name="plus" size={18} color={COLOR_TYPE_WHITE} />
            </span>
            <span className={`${cls}__create-btn-label`}>
              {t.milestones.createMilestone}
            </span>
          </button>
        )}
      </div>
      {isCreateModalOpen && (
        <ProjectMilestoneCreateModal
          key={`project-milestone-create-${projectSlug}`}
          projectId={projectId}
          onCancel={() => setIsCreateModalOpen(false)}
          onCreated={() => setIsCreateModalOpen(false)}
        />
      )}

      {!!editingMilestone && (
        <ProjectMilestoneCreateModal
          key={`project-milestone-edit-${editingMilestone.id}`}
          projectId={projectId}
          prefill={editingMilestone}
          onCancel={() => setEditingMilestone(null)}
          onCreated={() => setEditingMilestone(null)}
        />
      )}
      {milestones.length === 0 && (
        <div className={`${cls}__empty`}>{t.milestones.noMilestonesYet}</div>
      )}

      {milestones.length > 0 && (
        <>
          <div className={`${cls}__timeline`}>
            {milestones.map((milestone) => (
              <div className={`${cls}__timeline-item`} key={milestone.id}>
                <div className={`${cls}__timeline-marker`} aria-hidden />
                <div className={`${cls}__timeline-content`}>
                  <div className={`${cls}__milestone-title-row`}>
                    <div className={`${cls}__meta`}>
                      <span className={`${cls}__meta-item`}>
                        <Icon
                          name="calendar"
                          size={18}
                          color={COLOR_TYPE_GRAY_TINT_14}
                        />
                        <span>
                          {milestone.dateLabel ?? DateFormatter.asDayMonthYear(milestone.date)}
                        </span>
                      </span>
                    </div>
                    {milestone.canUpdate.value && (
                      <button
                        type="button"
                        className={`${cls}__edit-btn`}
                        onClick={() => setEditingMilestone(milestone)}
                      >
                        {t.milestones.editMilestone}
                      </button>
                    )}
                  </div>
                  <h3 className={`${cls}__milestone-title`}>
                    {milestone.title}
                  </h3>
                  {!!milestone.images[0]?.file.previewUrl && (
                    <div className={`${cls}__milestone-image`}>
                      <img
                        src={milestone.images[0]?.file.previewUrl}
                        alt=""
                        loading="lazy"
                        width="100%"
                        className="rounded-xl"
                      />
                    </div>
                  )}
                  {milestone.content.__typename === 'MarkdownContent'
                    ? (
                      <MarkdownRenderer
                        shortcodes={milestone.content.shortcodes}
                        text={milestone.content.text}
                      />
                    )
                    : (
                      <p
                        className={`${cls}__milestone-content`}
                        dangerouslySetInnerHTML={{
                          __html: makeHTMLLinksFromText(milestone.content.text ?? '')
                        }}
                      />
                    )}
                </div>
              </div>
            ))}
          </div>

          {withShowAllFooter && (
            <div className={`${cls}__show-all-footer`}>
              <NavigationItem
                className={`${cls}__show-all-link`}
                onClick={() => history.push(routes.milestones(projectSlug))}
              >
                <span className={`${cls}__show-all-label`}>
                  {t.milestones.showAll}
                </span>
              </NavigationItem>
            </div>
          )}
        </>
      )}
    </DetailCard>
  )
}
